import React, { useMemo } from 'react';
import { Card, CardContent, Typography, Box, useTheme, Stack, alpha, Skeleton } from '@mui/material';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// --- MODIFICATION: Define component props ---
interface ProviderData {
  provider: string;
  cost: number;
}

interface CostDistributionChartProps {
  providerData?: ProviderData[] | null;
  isLoading?: boolean;
}

// Solid colors for the legend
const COLORS: { [key: string]: string } = {
  GCP: ' rgba(0, 100, 255, 1)',
  AWS: '#FF8900',
  AZURE: '#94E9B8',
  LANGFUSE: '#FF3023', // <--- ADD THIS LINE
};

// Gradient definitions for the pie chart slices
const GRADIENTS: { [key: string]: { from: string; to: string } } = {
    GCP: { from: 'rgb(53, 160, 236)', to: 'rgba(0, 100, 255, 1)' }, //for gcp make chnages
    AWS: { from: '#FFC107', to: '#FF8900' },
    AZURE: { from: '#B3F0CF', to: '#94E9B8' },
    LANGFUSE: { from: '#FF6F61', to: '#FF3023' }, // <--- ADD THIS LINE (you can adjust 'from' for desired gradient effect)
};

const CustomTooltip = ({ active, payload }: any) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    // Validate data object and its properties
    if (!data || typeof data.name !== 'string' || typeof data.value !== 'number') {
        console.error("CostDistributionChart CustomTooltip: Invalid or missing data in payload:", data);
        return <Box sx={{ backgroundColor: alpha(theme.palette.common.white, 1), color: theme.palette.common.black, padding: '8px 12px', borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[3], textAlign: 'center' }}><Typography color="error">Invalid data</Typography></Box>;
    }
    return (
      <Box
        sx={{
          backgroundColor: alpha(theme.palette.common.white, 1),
          color: theme.palette.common.black,
          padding: '8px 12px',
          borderRadius: theme.shape.borderRadius,
          boxShadow: theme.shadows[3],
          textAlign: 'center',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>{data.name}</Typography>
        <Typography variant="body2">{`$${data.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</Typography>
      </Box>
    );
  }
  return null;
};


const CostDistributionChart: React.FC<CostDistributionChartProps> = ({ providerData, isLoading }) => {
  const theme = useTheme();

  // --- MODIFICATION: Transform and filter incoming data, memoize the result ---
  const chartData = useMemo(() => {
    if (!providerData) return [];
    
    return providerData
      .filter(item => item.cost > 0) // Only include providers with a cost
      .map(item => ({
        name: item.provider.toUpperCase(), // e.g., 'gcp' -> 'GCP'
        value: item.cost,
      }));
  }, [providerData]);

  const renderContent = () => {
    // --- MODIFICATION: Handle loading state ---
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 3, pt: 2 }}>
          <Skeleton variant="circular" width={160} height={160} />
          <Stack spacing={1} width="70%" sx={{ maxWidth: '200px' }}>
            <Skeleton variant="text" height={20} />
            <Skeleton variant="text" height={20} />
            <Skeleton variant="text" height={20} />
          </Stack>
        </Box>
      );
    }
    
    // --- MODIFICATION: Handle no data state ---
    if (chartData.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            No provider cost data available.
          </Typography>
        </Box>
      );
    }

    // --- MODIFICATION: Render dynamic chart when data is available ---
    return (
      <Box
        sx={{
          height: { xs: 280, sm: 300, md: 320 },
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <ResponsiveContainer width="100%" height="70%">
          <PieChart>
            <defs>
              {chartData.map((entry) => (
                <linearGradient key={`gradient-${entry.name}`} id={`gradient-${entry.name}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GRADIENTS[entry.name]?.from} stopOpacity={1} />
                  <stop offset="100%" stopColor={GRADIENTS[entry.name]?.to} stopOpacity={1} />
                </linearGradient>
              ))}
            </defs>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius="100%"
              innerRadius="45%"
              fill="#8884d8"
              dataKey="value"
              paddingAngle={2}
              cornerRadius={5}
              
            >
              {chartData.map((entry) => (
                <Cell
                  key={`cell-${entry.name}`}
                  fill={`url(#gradient-${entry.name})`}
                  stroke={theme.palette.background.paper}
                  strokeWidth={3}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
          </PieChart>
        </ResponsiveContainer>

        <Stack direction="column" spacing={1} sx={{ mt: 2, width: '70%', maxWidth: '200px', alignSelf: 'center' }}>
          {chartData.map((entry) => (
            <Box
              key={`legend-${entry.name}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FiberManualRecordIcon sx={{ color: COLORS[entry.name], fontSize: '12px', mr: 1 }} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {entry.name}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                {`$${entry.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <Card elevation={0} sx={{ borderRadius: '12px', height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, md: 2 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1,mt:1, fontSize:'16px' }}>
          Provider Cost Split
        </Typography>
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default CostDistributionChart;
